import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ApplicationConfigService } from '../config/application-config.service';
import { MatDialog } from '@angular/material/dialog';
import { SfzDialogComponent } from 'app/shared/sfz-dialog/sfz-dialog.component';
import { Role } from 'app/config/authority.constants';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  static USER_NAME_SESSION_ATTRIBUTE = 'authenticatedUserName';
  static JWT_SESSION_ATTRIBUTE = 'authenticatedUserJwt';        // este é usado pelo aplicativo
  static JWT_SESSION_ATTRIBUTE_JHI = 'jhi-authenticationToken'; // Este é usado pelo portal
  static NUM_PESSOA_SESSION_ATTRIBUTE = 'authenticatedNumPessoa';

  private static dialogRole: MatDialog;

  constructor(private http: HttpClient,
              private router: Router,
              private applicationConfigService: ApplicationConfigService,
              private dialog: MatDialog) {
                AuthenticationService.dialogRole = dialog;
              }

  static getStoredToken(): string | null {
    const possibleKeys = [
      AuthenticationService.JWT_SESSION_ATTRIBUTE,
      AuthenticationService.JWT_SESSION_ATTRIBUTE_JHI
    ];

    for (const key of possibleKeys) {
      const token = localStorage.getItem(key) ?? sessionStorage.getItem(key);
      if (token) {
        return token.replace(/^"(.*)"$/, '$1');
      }
    }

    return null;
  }

  static temPermissaoParaExibirItemMenu(role: string): boolean {
    return AuthenticationService.getRoleNoDialog(role);
  }

  static temPermissaoParaEmitirRelatorioOmissao(errorMessage?: string): boolean {
    return this.getRole(Role.ROLE_REL_OMISSAO, errorMessage); 
  }

  static temPermissaoParaEmitirRelatorioEntregaArquivos(errorMessage?: string): boolean {
    return this.getRole(Role.ROLE_REL_ENTREGA_DESTDA, errorMessage); 
  }

  static temPermissaoParaExibirValidarExtratoProcessamentoEFD(errorMessage?: string): boolean {
    return this.getRole(Role.ROLE_EFD_CONSULTA_EXTRATO_EFD, errorMessage);
  }

  static getRole(rolemName: string, errorMessage?: string): boolean {
    const token = AuthenticationService.getStoredToken();
    if (!token) {
      AuthenticationService.openDialog("Permissão negada", "Usuário sem acesso", "");
      return false; // Sem token, sem acesso
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles:string = payload.auth;
      if (!roles.includes(rolemName)) {
        if (errorMessage) {
          AuthenticationService.openDialog("Permissão negada", errorMessage, "");
        } else {
          AuthenticationService.openDialog("Permissão negada", "Você não tem permissão para acessar esta funcionalidade.", "");
        }
        return false; // Role não existe
      }

      return true; // Role existe no token
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      return false;
    }
  }

  static getRoleNoDialog(rolemName: string): boolean {
    const token = AuthenticationService.getStoredToken();
    if (!token) {
      return false; // Sem token, sem acesso
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles:string = payload.auth;
      if (!roles.includes(rolemName)) {
        return false; // Role não existe
      }

      return true; // Role existe no token
    } catch (error) {
      console.error('Erro ao decodificar o token:', error);
      return false;
    }
  }

  static openDialog(titulo: string, mensagem: string, detalhe: string): void {
    AuthenticationService.dialogRole.open(SfzDialogComponent, {
      data: { titulo, mensagem, detalhe }
    });
  }

  authenticate(credentials: any, callback?: any): void {
    const headers = new HttpHeaders({ 'X-Requested-With': 'XMLHttpRequest' });

    this.http
      .post(
        this.applicationConfigService.getEndpointFor('/auth/authenticate'),
        { username: credentials.login, password: credentials.senha },
        { headers }
      )
      .subscribe({
        next(result: any): void {
          sessionStorage.setItem(AuthenticationService.USER_NAME_SESSION_ATTRIBUTE, credentials.login);
          sessionStorage.setItem(AuthenticationService.JWT_SESSION_ATTRIBUTE_JHI, JSON.stringify(result.id_token));
          sessionStorage.setItem(AuthenticationService.NUM_PESSOA_SESSION_ATTRIBUTE, result.num_pessoa);
          callback(true, null);
        },
        error(error: any): void {
          callback(false, error);
        },
      });

  }

  isLoggedIn(): boolean {
    const userName = sessionStorage.getItem(AuthenticationService.USER_NAME_SESSION_ATTRIBUTE);
    const numPessoa = sessionStorage.getItem(AuthenticationService.NUM_PESSOA_SESSION_ATTRIBUTE);
    const token = AuthenticationService.getStoredToken();

    return !!userName && !!token && !!numPessoa;
  }

  logout(): void {
    const possibleKeys = [
      AuthenticationService.JWT_SESSION_ATTRIBUTE,
      AuthenticationService.JWT_SESSION_ATTRIBUTE_JHI
    ];

    sessionStorage.removeItem(AuthenticationService.USER_NAME_SESSION_ATTRIBUTE);
    sessionStorage.removeItem(AuthenticationService.NUM_PESSOA_SESSION_ATTRIBUTE);

    for (const key of possibleKeys) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
    this.router.navigate(['/login']);
  }

  getUsuarioSessao(): string | null {
    return sessionStorage.getItem(AuthenticationService.USER_NAME_SESSION_ATTRIBUTE);
  }

  // Add this static method
}
